import type { GoonchaChallenge, GoonchaPass } from "@gooner/shared";

/**
 * One request for a GOONCHA grid, and what it came back as.
 *
 * `/gooncha/new` either deals a grid, waves the viewer through with a pass (the degraded
 * path: the catalog could not grade a grid), or fails: an aborted request, a dropped
 * connection, a 5xx. The failure used to be swallowed in the component's catch: the row
 * folded back to its unchecked box with nothing on screen, the submit stayed locked, and
 * the viewer had no way to tell a broken check from a slow one. It is a value here so the
 * component has to handle it, and so a check can drive it without a browser.
 *
 * This only asks. It never retries on its own: the next request is the viewer's Retry,
 * so a server that is down sees one request per press and never a loop.
 */
export type GoonchaDeal =
  | { kind: "challenge"; challenge: GoonchaChallenge }
  | { kind: "pass"; token: string }
  | { kind: "error" };

/** What the row says when a grid could not be fetched. */
export const GOONCHA_LOAD_ERROR = "Couldn't load the robot check.";

export async function requestGoonchaDeal(
  fresh: () => Promise<GoonchaChallenge | GoonchaPass>,
): Promise<GoonchaDeal> {
  let got: GoonchaChallenge | GoonchaPass;
  try {
    got = await fresh();
  } catch {
    return { kind: "error" };
  }
  if ("token" in got) return { kind: "pass", token: got.token };
  return { kind: "challenge", challenge: got };
}
